import Link from 'next/link';
import { useState } from 'react';
import {
  LuMapPin,
  LuPhone,
  LuMail,
  LuClock,
  LuFacebook,
  LuInstagram,
  LuLinkedin,
  LuHouse,
  LuInfo,
  LuNewspaper,
  LuBookOpen,
  LuLoader,
  LuCheck,
  LuX,
} from 'react-icons/lu';
import { FaXTwitter } from 'react-icons/fa6';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [statusMessage, setStatusMessage] = useState('');

  // Newsletter subscription function
  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setSubscriptionStatus('error');
      setStatusMessage('Please enter a valid email address');
      return;
    }

    setSubscriptionStatus('loading');

    try {
      // Use window.location.origin to create an absolute URL
      const baseUrl = window.location.origin;
      console.log(
        `Attempting to subscribe at: ${baseUrl}/api/newsletter/subscribe`
      );

      const response = await fetch(`${baseUrl}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newsletterEmail,
          // Adding name as null since we don't collect it in this form
          name: null,
        }),
      });

      console.log(
        `Response status: ${response.status}, statusText: ${response.statusText}`
      );

      // Check if response is actually JSON (not a redirect to HTML)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Response is not JSON:', text.substring(0, 200));
        setSubscriptionStatus('error');
        setStatusMessage('Unexpected response from server. Please try again.');
        return;
      }

      const data = await response.json();
      console.log('Subscription response:', data);

      if (data.success) {
        setSubscriptionStatus('success');
        // Customize message to emphasize privacy
        const message = data.isNewUser
          ? 'Thank you for subscribing! Please check your email to verify your account. Your email is securely handled with Clerk and not stored in our database.'
          : data.message || 'Thank you for subscribing to our newsletter!';
        setStatusMessage(message);
        setNewsletterEmail(''); // Clear the input field
      } else {
        console.error('Subscription error:', data);
        setSubscriptionStatus('error');
        setStatusMessage(
          data.error || 'Failed to subscribe. Please try again.'
        );
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubscriptionStatus('error');
      setStatusMessage(
        error instanceof Error
          ? `Error: ${error.message}`
          : 'Failed to subscribe. Please try again.'
      );
    }
  };

  // Main services offered
  const mainServices = [
    { name: 'Land Survey', link: '/public/services/land-survey' },
    { name: 'GIS Mapping', link: '/public/services/gis-mapping' },
    {
      name: 'Property Management',
      link: '/public/services/property-management',
    },
    { name: 'Cyber Services', link: '/public/services/cyber-services' },
    { name: 'Qwetu Gas Supplier', link: '/public/services/qwetu-gas-supply' },
  ];

  // Main pages available in the site (only implemented pages)
  const mainPages = [
    { name: 'Home', link: '/', icon: <LuHouse className='mr-2 h-4 w-4' /> },
    {
      name: 'About Us',
      link: '/about',
      icon: <LuInfo className='mr-2 h-4 w-4' />,
    },
    {
      name: 'Contact',
      link: '/contact',
      icon: <LuMail className='mr-2 h-4 w-4' />,
    },
    {
      name: 'Services Overview',
      link: '/services',
      icon: <LuBookOpen className='mr-2 h-4 w-4' />,
    },
  ];

  // Legal & policy links
  const legalLinks = [
    { name: 'Privacy Policy', link: '/privacy-policy' },
    { name: 'Terms of Service', link: '/terms-of-service' },
  ];

  // Social media links
  const socialLinks = [
    {
      name: 'Facebook',
      icon: <LuFacebook className='h-5 w-5' />,
      link: 'https://www.facebook.com/Qwetulands?_rdc=1&_rdr#',
    },
    {
      name: 'Twitter',
      icon: <FaXTwitter className='h-5 w-5' />,
      link: 'https://x.com/Qwetulands',
    },
    {
      name: 'LinkedIn',
      icon: <LuLinkedin className='h-5 w-5' />,
      link: 'https://www.linkedin.com/in/joseph-mburu/',
    },
  ];

  return (
    <footer className='relative bg-gray-900 text-gray-200'>
      {/* Decorative Top Border */}
      <div className='absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050]' />

      <div className='mx-auto max-w-7xl px-4 pt-16 pb-8 sm:px-6 lg:px-8'>
        <div className='grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4'>
          {/* Company Info */}
          <div className='space-y-6'>
            <div>
              <Link href='/'>
                <img
                  src='/images/logo_clear.png'
                  alt='Qwetulands'
                  className='h-12 w-auto bg-white p-2 rounded-lg'
                />
              </Link>
              <p className='mt-4 max-w-xs text-sm text-gray-400'>
                Professional services for land, property, and technology
                solutions. Your trusted partner in development and management.
              </p>
            </div>
            <div className='space-y-3'>
              <p className='flex items-center text-sm'>
                <LuMapPin className='mr-3 h-5 w-5 text-[#00B0F0]' />
                Malindi, Opposite Mbuzi Wengi
              </p>
              <p className='flex items-center text-sm'>
                <LuPhone className='mr-3 h-5 w-5 text-[#00B0F0]' />
                <a
                  href='tel:+254756211546'
                  className='hover:text-[#00B0F0] transition-colors'
                >
                  +254 756 211 546
                </a>
              </p>
              <p className='flex items-center text-sm'>
                <LuMail className='mr-3 h-5 w-5 text-[#00B0F0]' />
                <a
                  href='mailto:info@qwetulands.com'
                  className='hover:text-[#00B0F0] transition-colors'
                >
                  info@qwetulands.com
                </a>
              </p>
              <p className='flex items-center text-sm'>
                <LuClock className='mr-3 h-5 w-5 text-[#00B0F0]' />
                Mon - Sat: 8:00 AM - 6:00 PM
              </p>
            </div>
          </div>

          {/* Main Pages */}
          <div>
            <h3 className='text-lg font-semibold text-white'>Quick Links</h3>
            <ul className='mt-4 space-y-2'>
              {mainPages.map((page) => (
                <li key={page.name}>
                  <Link
                    href={page.link}
                    className='text-sm text-gray-400 transition-colors hover:text-[#00B0F0] flex items-center'
                  >
                    {page.icon}
                    {page.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Column - Consolidated */}
          <div>
            <h3 className='text-lg font-semibold text-white'>Our Services</h3>
            <ul className='mt-4 space-y-3'>
              {mainServices.map((service) => (
                <li key={service.name}>
                  <Link
                    href={service.link}
                    className='text-sm text-gray-400 transition-colors hover:text-[#00B0F0]'
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Signup - Updated */}
          <div>
            <h3 className='text-lg font-semibold text-white'>Newsletter</h3>
            <p className='mt-4 text-sm text-gray-400'>
              Subscribe to our newsletter for updates and insights.
            </p>
            <form className='mt-4' onSubmit={handleNewsletterSubscribe}>
              <div className='flex max-w-md flex-col gap-4'>
                <div className='w-full'>
                  <input
                    type='email'
                    placeholder='Enter your email'
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className={`w-full rounded-lg bg-gray-800 px-4 py-2 text-sm text-gray-200 placeholder-gray-400 focus:outline-hidden focus:ring-2 ${
                      subscriptionStatus === 'error'
                        ? 'border border-red-500 focus:ring-red-500'
                        : 'focus:ring-[#00B0F0]'
                    }`}
                    disabled={
                      subscriptionStatus === 'loading' ||
                      subscriptionStatus === 'success'
                    }
                  />
                </div>

                {subscriptionStatus === 'error' && (
                  <div className='flex items-start bg-red-900/20 border border-red-700/30 rounded-md p-2'>
                    <LuX className='h-5 w-5 text-red-400 shrink-0 mt-0.5 mr-2' />
                    <p className='text-xs text-red-300'>{statusMessage}</p>
                  </div>
                )}

                {subscriptionStatus === 'success' && (
                  <div className='flex items-start bg-green-900/20 border border-green-700/30 rounded-md p-2'>
                    <LuCheck className='h-5 w-5 text-green-400 shrink-0 mt-0.5 mr-2' />
                    <p className='text-xs text-green-300'>{statusMessage}</p>
                  </div>
                )}

                <button
                  type='submit'
                  disabled={
                    subscriptionStatus === 'loading' ||
                    subscriptionStatus === 'success'
                  }
                  className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                    subscriptionStatus === 'success'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-[#00B0F0] hover:bg-[#0090D0]'
                  } ${
                    subscriptionStatus === 'loading' ||
                    subscriptionStatus === 'success'
                      ? 'opacity-70 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {subscriptionStatus === 'loading' ? (
                    <>
                      <LuLoader className='inline-block mr-2 h-4 w-4 animate-spin' />
                      Processing...
                    </>
                  ) : subscriptionStatus === 'success' ? (
                    <>
                      <LuCheck className='inline-block mr-2 h-4 w-4' />
                      Subscribed
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className='mt-12 border-t border-gray-800 pt-8'>
          <div className='flex flex-col items-center justify-between gap-6 sm:flex-row'>
            <p className='text-sm text-gray-400'>
              &copy; {currentYear} Qwetulands. All rights reserved.
            </p>

            {/* Social Links */}
            <div className='flex space-x-6'>
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.link}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-gray-400 transition-colors hover:text-[#00B0F0]'
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>

            {/* Terms & Privacy Links */}
            <div className='flex space-x-6'>
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  href={link.link}
                  className='text-sm text-gray-400 transition-colors hover:text-[#00B0F0]'
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
